/**
 * The Top Gifts table (GFG-272) — purely presentational, no state/hooks. This tab does not use
 * the shared `Blotter` (it always renders search + pagination, and 11 screens depend on that
 * shape); this table mirrors its visual language instead — th/td below are copied from
 * `Blotter.tsx:229` (`th`) and `Blotter.tsx:249` (`td`), and `blotter-row` (hover highlight) is
 * reused from `index.css:364` so the two tables still look like one product.
 */
import CircularProgress from "@mui/material/CircularProgress";
import type { CSSProperties } from "react";
import { MIN_WIDTH } from "@/constants/reportsTopGifts";
import type { TopGiftColumn, TopGiftRow } from "@/interfaces/reportsTopGifts";

interface TopGiftsTableProps {
  columns: TopGiftColumn[];
  rows: TopGiftRow[];
  isLoading: boolean;
  emptyMessage: string;
  minWidth?: number;
}

// Copied from Blotter.tsx:229 (`th`) — same wax header band / rodeo-black heading style, kept
// separate so this table's markup doesn't couple to the 11 screens that share `Blotter`.
const th: CSSProperties = {
  textAlign: "left",
  padding: "12px 16px",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "var(--color-rodeo-black)",
  whiteSpace: "nowrap",
  userSelect: "none",
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "var(--color-wax)",
  boxShadow: "inset 0 -1px 0 var(--color-smoke)",
};

// Copied from Blotter.tsx:249 (`td`) — same reason as `th` above.
const td: CSSProperties = {
  padding: "14px 16px",
  fontSize: 14,
  color: "var(--color-rodeo-black)",
  borderBottom: "1px solid var(--color-smoke)",
  whiteSpace: "nowrap",
};

export default function TopGiftsTable({
  columns,
  rows,
  isLoading,
  emptyMessage,
  minWidth = MIN_WIDTH,
}: TopGiftsTableProps) {
  const colCount = columns.length;

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", minWidth }}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.key} style={{ ...th, ...column.headerStyle }}>
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={colCount} style={{ ...td, textAlign: "center", padding: 40 }}>
              <CircularProgress aria-label="Loading rows" size={22} />
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td
              colSpan={colCount}
              style={{ ...td, textAlign: "center", padding: 40, color: "var(--color-graphite)" }}
            >
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr key={row.productId} className="blotter-row">
              {columns.map((column) => (
                <td key={column.key} style={{ ...td, ...column.cellStyle }}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
